export const NETWORK_OFFLINE_CODE = "network/offline";
export const NETWORK_UNREACHABLE_CODE = "network/unreachable";

const DEFAULT_REACHABILITY_TIMEOUT_MS = 3000;
const DEFAULT_REACHABILITY_PATH = "/manifest.json";

function toNetworkError(code, message) {
  const err = new Error(message);
  err.code = code;
  return err;
}

function buildReachabilityUrl(path = DEFAULT_REACHABILITY_PATH) {
  const separator = String(path).includes("?") ? "&" : "?";
  return `${path}${separator}reachability=${Date.now()}`;
}

export async function checkNetworkReachability({
  url = DEFAULT_REACHABILITY_PATH,
  timeoutMs = DEFAULT_REACHABILITY_TIMEOUT_MS,
} = {}) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw toNetworkError(NETWORK_OFFLINE_CODE, "Device is offline.");
  }

  if (typeof fetch !== "function") {
    return { ok: true, skipped: true };
  }

  const supportsAbort = typeof AbortController !== "undefined";
  const controller = supportsAbort ? new AbortController() : null;
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      try {
        controller?.abort();
      } catch {
        // ignore abort failures
      }
      reject(toNetworkError(NETWORK_UNREACHABLE_CODE, "Network probe timed out."));
    }, timeoutMs);
  });

  const fetchPromise = fetch(buildReachabilityUrl(url), {
    cache: "no-store",
    credentials: "same-origin",
    signal: controller?.signal,
  });

  try {
    const response = await Promise.race([fetchPromise, timeoutPromise]);
    if (!response?.ok) {
      throw toNetworkError(NETWORK_UNREACHABLE_CODE, "Network probe failed.");
    }
    return { ok: true };
  } catch (err) {
    if (err?.code === NETWORK_UNREACHABLE_CODE) throw err;
    throw toNetworkError(NETWORK_UNREACHABLE_CODE, "Network probe failed.");
  } finally {
    clearTimeout(timeoutId);
    try {
      controller?.abort();
    } catch {
      // ignore abort failures
    }
  }
}
